import React, { useState, useCallback } from 'react';
import { UnifiedTransaction } from '../types/payment';
// Single source of truth for the filter shape lives in the hook — keep them in sync
// by importing it here rather than re-declaring it (they drifted before and broke the build).
import { TransactionFilterState } from '../hooks/useTransactionFilters';
import { ALLOWED_CATEGORIES, US_STATES } from '../constants/enums';
import { TransactionFilterOptions } from '../services/api';
import './UserFilters.css';

interface TransactionFiltersProps {
  filters: TransactionFilterState;
  updateFilter: (field: keyof TransactionFilterState, value: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  transactions: UnifiedTransaction[];
  filterStats: {
    total: number;
    filtered: number;
    hidden: number;
  };
  showVoidRefundFilters?: boolean; // Only show void/refund filters in All Transactions view
  showProcessingStatusFilter?: boolean; // Only show processing status in Pending Processing view
  showSaleFilters?: boolean; // Show sale state and category filters (mainly for Pending Processing)
  // Choices over the whole dataset (server-paged pages). Merged with the loaded
  // rows; without it the dropdowns only offer values found on the current page.
  options?: TransactionFilterOptions | null;
}

const mergeValues = (page: (string | undefined | null)[], all: string[] = []): string[] =>
  Array.from(new Set([...all, ...page.filter(Boolean) as string[]])).sort();
const mergePeople = (page: { id: string; name: string }[], all: { id: string; name: string }[] = []) =>
  [...all, ...page]
    .filter((u, i, self) => i === self.findIndex((x) => x.id === u.id))
    .sort((a, b) => a.name.localeCompare(b.name));

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  filters,
  updateFilter,
  clearFilters,
  hasActiveFilters,
  transactions,
  filterStats,
  showVoidRefundFilters = false,
  showProcessingStatusFilter = false,
  showSaleFilters = true,
  options
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  // Filters are applied EXPLICITLY (Search button / Enter key), not on every
  // keystroke. `localFilters` is a draft the user edits freely; nothing is
  // fetched until applyFilters pushes the draft up to the parent `filters`,
  // whose change is what triggers the (server-side) reload.
  const applyFilters = useCallback(() => {
    (Object.keys(localFilters) as (keyof TransactionFilterState)[]).forEach((key) => {
      if (filters[key] !== localFilters[key]) {
        updateFilter(key, localFilters[key]);
      }
    });
  }, [localFilters, filters, updateFilter]);

  // Mirror the draft when the parent resets/replaces filters externally
  // (Clear Filters, or navigating in with a preset). While the user is only
  // editing the draft, `filters` is unchanged so this does not clobber input.
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // True when the draft differs from what's actually applied.
  const hasPendingChanges = React.useMemo(
    () => (Object.keys(localFilters) as (keyof TransactionFilterState)[])
      .some((key) => localFilters[key] !== filters[key]),
    [localFilters, filters]
  );

  // Handle local (draft) filter changes
  const handleFilterChange = useCallback((field: keyof TransactionFilterState, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    // Reset the parent immediately; the sync effect mirrors it into the draft.
    clearFilters();
  }, [clearFilters]);

  // Get unique values for dropdowns
  const uniqueTxTypes = React.useMemo(() => {
    return mergeValues(transactions.map(tx => tx.tx_type), options?.tx_types);
  }, [transactions, options]);

  const uniqueStatuses = React.useMemo(() => {
    return Array.from(new Set(transactions.map(tx => tx.status).filter(Boolean))).sort() as string[];
  }, [transactions]);

  const uniqueCardBrands = React.useMemo(() => {
    return mergeValues(transactions.map(tx => tx.card_brand), options?.card_brands);
  }, [transactions, options]);

  const uniqueCreatedBy = React.useMemo(() => {
    // Unique created_by (UID) + created_by_name pairs for filtering
    const page = transactions
      .filter(tx => tx.created_by)
      .map(tx => ({ id: tx.created_by!, name: tx.created_by_name || tx.created_by! }));
    return mergePeople(page, options?.created_by);
  }, [transactions, options]);

  const uniqueResponseCodes = React.useMemo(() => {
    return mergeValues(transactions.map(tx => tx.nmi_response_code || tx.response_code), options?.response_codes);
  }, [transactions, options]);

  // Use the canonical Sale Type / Sale State lists (same as the payment form) rather
  // than values derived from the loaded page — otherwise server-side pagination would
  // only ever offer the categories/states present on the current page.
  const uniqueCategories = React.useMemo(() => [...ALLOWED_CATEGORIES] as string[], []);

  const uniqueSaleStates = React.useMemo(() => [...US_STATES] as string[], []);

  const uniqueTfns = React.useMemo(() => {
    return mergeValues(transactions.map(tx => tx.tfn), options?.tfns);
  }, [transactions, options]);

  const uniqueVoidedRefundedBy = React.useMemo(() => {
    // Get unique combinations of voided/refunded users
    const users = transactions
      .filter(tx => tx.voided_by || tx.refunded_by)
      .map(tx => {
        if (tx.voided_by) {
          return {
            id: tx.voided_by,
            name: tx.voided_by_name || tx.voided_by
          };
        } else if (tx.refunded_by) {
          return {
            id: tx.refunded_by,
            name: tx.refunded_by_name || tx.refunded_by
          };
        }
        return null;
      })
      .filter(Boolean)
      .filter((user, index, self) => 
        user && index === self.findIndex(u => u && u.id === user.id)
      )
      .sort((a, b) => a!.name.localeCompare(b!.name));

    return mergePeople(users.filter(Boolean) as Array<{id: string; name: string}>, options?.voided_refunded_by);
  }, [transactions, options]);

  const getUniqueProcessors = React.useCallback(() => {
    // Get unique processors (assigned_to_processor_name)
    return Array.from(new Set(
      transactions
        .map(tx => (tx as any).assigned_to_processor_name)
        .filter(Boolean)
    )).sort() as string[];
  }, [transactions]);

  const getUniqueAssigners = React.useCallback(() => {
    // Get unique assigners (assigned_by_name)
    return Array.from(new Set(
      transactions
        .map(tx => (tx as any).assigned_by_name)
        .filter(Boolean)
    )).sort() as string[];
  }, [transactions]);

  return (
    <div className="user-filters">
      <div className="filters-header">
        <div className="filters-info">
          <h4>Filter Transactions</h4>
          <div className="filter-stats">
            <span className="stats-text">
              Showing <strong>{filterStats.filtered}</strong> of <strong>{filterStats.total}</strong> transactions
              {filterStats.hidden > 0 && (
                <span className="hidden-count"> ({filterStats.hidden} filtered out)</span>
              )}
            </span>
          </div>
        </div>
        
        <div className="filters-controls">
          {hasActiveFilters && (
            <button 
              onClick={handleClearFilters}
              className="clear-filters-btn"
              title="Clear all filters"
            >
              🗑️ Clear Filters ({Object.values(filters).filter(v => v !== '').length})
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`toggle-filters-btn ${isExpanded ? 'expanded' : ''}`}
            title={isExpanded ? 'Hide filters' : 'Show filters'}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="filter-icon"
            >
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"/>
            </svg>
            Filters
          </button>
        </div>
      </div>

      {isExpanded && (
        <div
          className="filters-content"
          onKeyDown={(e) => {
            // Enter from any text/number/date input applies the filters.
            if (e.key === 'Enter') {
              e.preventDefault();
              applyFilters();
            }
          }}
        >
          {/* Global Search */}
          <div className="filter-row">
            <div className="filter-group global-search">
              <label htmlFor="globalSearch">🔍 Global Search</label>
              <input
                id="globalSearch"
                type="text"
                value={localFilters.globalSearch}
                onChange={(e) => handleFilterChange('globalSearch', e.target.value)}
                placeholder="Search across all fields..."
                className="filter-input global-search-input"
              />
              <small className="filter-hint">
                Search transaction ID, customer name, card info, amounts, and more
              </small>
            </div>
          </div>

          {/* User and Transaction Type Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="createdByFilter">Created By</label>
              <select
                id="createdByFilter"
                value={localFilters.createdBy}
                onChange={(e) => handleFilterChange('createdBy', e.target.value)}
                className="filter-select"
              >
                <option value="">All Users</option>
                {uniqueCreatedBy.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="txTypeFilter">Transaction Type</label>
              <select
                id="txTypeFilter"
                value={localFilters.txType}
                onChange={(e) => handleFilterChange('txType', e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                {uniqueTxTypes.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="statusFilter">Status</label>
              <select
                id="statusFilter"
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="">All Status</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {showProcessingStatusFilter && (
              <div className="filter-group">
                <label htmlFor="processingStatusFilter">Processing Status</label>
                <select
                  id="processingStatusFilter"
                  value={localFilters.processingStatus}
                  onChange={(e) => handleFilterChange('processingStatus', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Processing Status</option>
                  <option value="PENDING_ASSIGNMENT">Pending Assignment</option>
                  <option value="ASSIGNED">Assigned</option>
                  <option value="IN_PROCESS">In Process</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
            )}

            {showProcessingStatusFilter && (
              <div className="filter-group">
                <label htmlFor="processedByFilter">Processed By</label>
                <select
                  id="processedByFilter"
                  value={localFilters.processedBy}
                  onChange={(e) => handleFilterChange('processedBy', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Processors</option>
                  {getUniqueProcessors().map(processor => (
                    <option key={processor} value={processor}>{processor}</option>
                  ))}
                </select>
              </div>
            )}

            {showProcessingStatusFilter && (
              <div className="filter-group">
                <label htmlFor="processorAssignedByFilter">Processor Assigned By</label>
                <select
                  id="processorAssignedByFilter"
                  value={localFilters.processorAssignedBy}
                  onChange={(e) => handleFilterChange('processorAssignedBy', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Assigners</option>
                  {getUniqueAssigners().map(assigner => (
                    <option key={assigner} value={assigner}>{assigner}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="filter-group">
              <label htmlFor="responseCodeFilter">Response Code</label>
              <select
                id="responseCodeFilter"
                value={localFilters.responseCode}
                onChange={(e) => handleFilterChange('responseCode', e.target.value)}
                className="filter-select"
              >
                <option value="">All Codes</option>
                {uniqueResponseCodes.map(code => (
                  <option key={code} value={code}>
                    {code} {code === '100' ? '(Success)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Sale State, Sale Type, Card Brand and TFN Row */}
          {(showSaleFilters || uniqueCardBrands.length > 0 || uniqueTfns.length > 0) && (
            <div className="filter-row">
              {showSaleFilters && (
                <>
                  <div className="filter-group">
                    <label htmlFor="saleStateFilter">Sale State</label>
                    <select
                      id="saleStateFilter"
                      value={localFilters.saleState}
                      onChange={(e) => handleFilterChange('saleState', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">All States</option>
                      {uniqueSaleStates.map(state => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label htmlFor="categoryFilter">Sale Type</label>
                    <select
                      id="categoryFilter"
                      value={localFilters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">All Types</option>
                      {uniqueCategories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="filter-group">
                <label htmlFor="cardBrandFilter">Card Brand</label>
                <select
                  id="cardBrandFilter"
                  value={localFilters.cardBrand}
                  onChange={(e) => handleFilterChange('cardBrand', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Brands</option>
                  {uniqueCardBrands.map(brand => (
                    <option key={brand} value={brand}>
                      {brand.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {uniqueTfns.length > 0 && (
                <div className="filter-group">
                  <label htmlFor="tfnFilter">TFN</label>
                  <select
                    id="tfnFilter"
                    value={localFilters.tfn}
                    onChange={(e) => handleFilterChange('tfn', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">All TFNs</option>
                    {uniqueTfns.map(tfn => (
                      <option key={tfn} value={tfn}>
                        {tfn}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Void/Refund Filters - Only show in All Transactions view */}
          {showVoidRefundFilters && (
            <div className="filter-row">
              <div className="filter-group">
                <label htmlFor="voidRefundStatusFilter">Void/Refund Status</label>
                <select
                  id="voidRefundStatusFilter"
                  value={localFilters.voidRefundStatus}
                  onChange={(e) => handleFilterChange('voidRefundStatus', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Transactions</option>
                  <option value="active">Active (Not Voided/Refunded)</option>
                  <option value="voided">Voided</option>
                  <option value="refunded">Refunded</option>
                  <option value="void_refund">Voided or Refunded</option>
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="voidedRefundedByFilter">Voided/Refunded By</label>
                <select
                  id="voidedRefundedByFilter"
                  value={localFilters.voidedRefundedBy}
                  onChange={(e) => handleFilterChange('voidedRefundedBy', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Users</option>
                  {uniqueVoidedRefundedBy.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Amount Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="amountExactFilter">Exact Amount ($)</label>
              <input
                id="amountExactFilter"
                type="number"
                step="0.01"
                min="0"
                value={localFilters.amountExact}
                onChange={(e) => handleFilterChange('amountExact', e.target.value)}
                placeholder="e.g. 100.00"
                className="filter-input"
              />
              <small className="filter-hint">Search for exact amount</small>
            </div>

            <div className="filter-group">
              <label htmlFor="amountMinFilter">Min Amount ($)</label>
              <input
                id="amountMinFilter"
                type="number"
                step="0.01"
                min="0"
                value={localFilters.amountMin}
                onChange={(e) => handleFilterChange('amountMin', e.target.value)}
                placeholder="e.g. 50.00"
                className="filter-input"
              />
              <small className="filter-hint">Greater than or equal to</small>
            </div>

            <div className="filter-group">
              <label htmlFor="amountMaxFilter">Max Amount ($)</label>
              <input
                id="amountMaxFilter"
                type="number"
                step="0.01"
                min="0"
                value={localFilters.amountMax}
                onChange={(e) => handleFilterChange('amountMax', e.target.value)}
                placeholder="e.g. 500.00"
                className="filter-input"
              />
              <small className="filter-hint">Less than or equal to</small>
            </div>
          </div>

          {/* Card and SSN Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="last4Filter">Last 4 Digits (Card)</label>
              <input
                id="last4Filter"
                type="text"
                maxLength={4}
                value={localFilters.last4}
                onChange={(e) => handleFilterChange('last4', e.target.value.replace(/\D/g, ''))}
                placeholder="1234"
                className="filter-input"
              />
              <small className="filter-hint">Last 4 digits of card</small>
            </div>

            <div className="filter-group">
              <label htmlFor="binFilter">BIN (First 6)</label>
              <input
                id="binFilter"
                type="text"
                maxLength={6}
                value={localFilters.bin}
                onChange={(e) => handleFilterChange('bin', e.target.value.replace(/\D/g, ''))}
                placeholder="424242"
                className="filter-input"
              />
              <small className="filter-hint">First 6 digits (BIN) of card</small>
            </div>

            <div className="filter-group">
              <label htmlFor="last4SsnFilter">Last 4 SSN</label>
              <input
                id="last4SsnFilter"
                type="text"
                maxLength={4}
                value={localFilters.last4Ssn}
                onChange={(e) => handleFilterChange('last4Ssn', e.target.value.replace(/\D/g, ''))}
                placeholder="1234"
                className="filter-input"
              />
              <small className="filter-hint">Last 4 digits of SSN</small>
            </div>
          </div>

          {/* Date of Birth (search by any part) */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="dobMonthFilter">DOB Month</label>
              <input
                id="dobMonthFilter"
                type="text"
                maxLength={2}
                value={localFilters.dobMonth}
                onChange={(e) => handleFilterChange('dobMonth', e.target.value.replace(/\D/g, ''))}
                placeholder="MM"
                className="filter-input"
              />
              <small className="filter-hint">Birth month (1-12)</small>
            </div>
            <div className="filter-group">
              <label htmlFor="dobDayFilter">DOB Day</label>
              <input
                id="dobDayFilter"
                type="text"
                maxLength={2}
                value={localFilters.dobDay}
                onChange={(e) => handleFilterChange('dobDay', e.target.value.replace(/\D/g, ''))}
                placeholder="DD"
                className="filter-input"
              />
              <small className="filter-hint">Birth day (1-31)</small>
            </div>
            <div className="filter-group">
              <label htmlFor="dobYearFilter">DOB Year</label>
              <input
                id="dobYearFilter"
                type="text"
                maxLength={4}
                value={localFilters.dobYear}
                onChange={(e) => handleFilterChange('dobYear', e.target.value.replace(/\D/g, ''))}
                placeholder="YYYY"
                className="filter-input"
              />
              <small className="filter-hint">Birth year</small>
            </div>
          </div>

          {/* Customer / billing filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="emailFilter">Email</label>
              <input
                id="emailFilter"
                type="text"
                value={localFilters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                placeholder="name@example.com"
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="phoneFilter">Phone</label>
              <input
                id="phoneFilter"
                type="text"
                value={localFilters.phone}
                onChange={(e) => handleFilterChange('phone', e.target.value)}
                placeholder="555-123-4567"
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="dlFilter">Driver's License</label>
              <input
                id="dlFilter"
                type="text"
                value={localFilters.dl}
                onChange={(e) => handleFilterChange('dl', e.target.value)}
                placeholder="DL number"
                className="filter-input"
              />
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="addressFilter">Address</label>
              <input
                id="addressFilter"
                type="text"
                value={localFilters.address}
                onChange={(e) => handleFilterChange('address', e.target.value)}
                placeholder="Street address"
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="cityFilter">City</label>
              <input
                id="cityFilter"
                type="text"
                value={localFilters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                placeholder="City"
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="billingStateFilter">State (Billing)</label>
              <input
                id="billingStateFilter"
                type="text"
                value={localFilters.billingState}
                onChange={(e) => handleFilterChange('billingState', e.target.value)}
                placeholder="e.g. NY"
                className="filter-input"
              />
            </div>
            <div className="filter-group">
              <label htmlFor="zipFilter">ZIP</label>
              <input
                id="zipFilter"
                type="text"
                value={localFilters.zip}
                onChange={(e) => handleFilterChange('zip', e.target.value)}
                placeholder="ZIP code"
                className="filter-input"
              />
            </div>
          </div>

          {/* Date Range Filters */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="dateFromFilter">Date From</label>
              <input
                id="dateFromFilter"
                type="date"
                value={localFilters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="filter-input date-input"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="dateToFilter">Date To</label>
              <input
                id="dateToFilter"
                type="date"
                value={localFilters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="filter-input date-input"
              />
            </div>
          </div>

          {/* Apply / Search — filters run only when clicked (or on Enter) */}
          <div className="filter-actions">
            <button
              type="button"
              className="apply-filters-btn"
              onClick={applyFilters}
              disabled={!hasPendingChanges}
            >
              🔍 Search
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                className="reset-filters-btn"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            )}
            {hasPendingChanges && (
              <span className="pending-hint">Unapplied changes — click Search (or press Enter) to apply</span>
            )}
          </div>

          {/* Filter Tips */}
          <div className="filter-tips">
            <small>
              💡 <strong>Tips:</strong> Enter your filter values, then click <strong>Search</strong> (or press Enter) to apply — filtering no longer runs as you type.
              Amount filters: use exact for precise matching, or min/max for ranges. All text filters support partial matching (case-insensitive).
            </small>
          </div>
        </div>
      )}

      <style>{`
        .transaction-filters {
          background: var(--bg-card);
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
          margin-bottom: 1.5rem;
        }

        .filters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .filters-info h4 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 1.125rem;
          font-weight: 600;
        }

        .filter-stats {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .hidden-count {
          color: var(--warning);
          font-weight: 500;
        }

        .filters-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .expanded {
          background: var(--primary-hover) !important;
        }

        .filter-icon {
          transition: transform 0.2s;
        }

        .expanded .filter-icon {
          transform: rotate(180deg);
        }

        .filters-content {
          padding: 1.5rem;
        }

        .filter-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .filter-row:last-of-type {
          margin-bottom: 1rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
        }

        .filter-group.global-search {
          grid-column: 1 / -1;
        }

        .filter-group label {
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .filter-input,
        .filter-select {
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.875rem;
        }

        .filter-input:focus,
        .filter-select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px var(--primary-light);
        }

        .global-search-input {
          font-size: 1rem;
          padding: 1rem;
        }

        .filter-hint {
          margin-top: 0.25rem;
          color: var(--text-secondary);
          font-size: 0.75rem;
        }

        .filter-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .apply-filters-btn {
          padding: 0.6rem 1.5rem;
          background: var(--primary);
          color: #fff;
          border: none;
          border-radius: 4px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, opacity 0.2s;
        }

        .apply-filters-btn:hover:not(:disabled) {
          background: var(--primary-hover);
        }

        .apply-filters-btn:disabled {
          opacity: 0.5;
          cursor: default;
        }

        .reset-filters-btn {
          padding: 0.6rem 1.25rem;
          background: transparent;
          color: var(--text-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          font-size: 0.9rem;
          cursor: pointer;
        }

        .reset-filters-btn:hover {
          background: var(--bg-secondary);
        }

        .pending-hint {
          color: var(--warning);
          font-size: 0.8rem;
          font-weight: 500;
        }

        .filter-tips {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: 4px;
          border-left: 4px solid var(--primary);
        }

        .filter-tips small {
          color: var(--text-secondary);
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .filters-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .filters-controls {
            justify-content: space-between;
          }

          .filter-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};